<template>
  <div class="settings-page">
    <h2>Apps</h2>
    <div class="subtitle">
      Download Routine on your computer and phone for the best experience
      everywhere.
    </div>

    <section v-for="group in groups" :key="group.title">
      <h3>{{ group.title }}</h3>
      <div
        v-for="platform in group.platforms"
        :key="platform.name"
        class="entry"
      >
        <div class="platform">
          <div class="platform-icon">
            <component :is="platform.icon" />
          </div>
          <div class="details">
            <label>{{ platform.name }}</label>
            <div
              v-if="platform.help || platform.helpLinks?.length"
              class="help"
            >
              {{ platform.help }}
              <span
                v-for="link in platform.helpLinks ?? []"
                :key="link.url"
                class="link"
                @click="() => openUrl(link.url)"
                >{{ link.label }}</span
              >
            </div>
          </div>
        </div>
        <div class="actions">
          <BaseButton
            v-for="download in platform.downloads"
            :key="download.url"
            kind="main"
            type="secondary"
            :text="download.label"
            @click="() => openUrl(download.url)"
          />
        </div>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import { Component, defineComponent } from "vue";

import BaseButton from "@/components/base/BaseButton.vue";
import AndroidIcon from "@/components/icons/apps/AndroidIcon.vue";
import AppleIcon from "@/components/icons/apps/AppleIcon.vue";
import IosIcon from "@/components/icons/apps/IosIcon.vue";
import LinuxIcon from "@/components/icons/apps/LinuxIcon.vue";
import MicrosoftIcon from "@/components/icons/apps/MicrosoftIcon.vue";
import * as inject from "@/components/inject";
import { openExternalUrl } from "@/utils/open-external-url";

type Link = { label: string; url: string };

type Platform = {
  name: string;
  icon: Component;
  help?: string;
  helpLinks?: Link[];
  downloads: Link[];
};

type PlatformGroup = { title: string; platforms: Platform[] };

const groups: PlatformGroup[] = [
  {
    title: "Desktop",
    platforms: [
      {
        name: "macOS",
        icon: AppleIcon,
        helpLinks: [
          {
            label: "Download for older Intel Mac computers",
            url: "https://releases.routine.co/routine/osx/Routine.dmg",
          },
        ],
        downloads: [
          {
            label: "Download",
            url: "https://releases.routine.co/routine/osx-arm64/Routine.dmg",
          },
        ],
      },
      {
        name: "Windows",
        icon: MicrosoftIcon,
        downloads: [
          {
            label: "Download",
            url: "https://releases.routine.co/routine/win32/Routine%20Setup.exe",
          },
        ],
      },
      {
        name: "Linux",
        icon: LinuxIcon,
        downloads: [
          {
            label: "Download",
            url: "https://releases.routine.co/routine/linux/Routine.AppImage",
          },
        ],
      },
    ],
  },
  {
    title: "Mobile",
    platforms: [
      {
        name: "iOS",
        icon: IosIcon,
        downloads: [
          {
            label: "Download",
            url: "https://apps.apple.com/us/app/routine-calendars-tasks/id1548507969",
          },
        ],
      },
      {
        name: "Android",
        icon: AndroidIcon,
        downloads: [
          {
            label: "Download",
            url: "https://play.google.com/store/apps/details?id=co.routine.android",
          },
        ],
      },
    ],
  },
];

export default defineComponent({
  components: {
    BaseButton,
  },
  setup() {
    const controller = inject.controller();

    const openUrl = (url: string) => openExternalUrl(url, controller);

    return {
      groups,
      openUrl,
    };
  },
});
</script>

<style scoped lang="scss">
@use "../../styles/settings.scss";

.platform {
  display: flex;
  align-items: center;
  column-gap: 12px;

  .platform-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    flex-shrink: 0;
    border-radius: $border-radius-m;
    @include themed() {
      background-color: t($grey-05);
    }

    svg {
      width: 16px;
      height: 16px;
    }
  }

  .details {
    display: flex;
    flex-direction: column;

    .help .link {
      cursor: pointer;
      text-decoration: underline;
      @include transition(color 0.1s ease-out);

      &:hover {
        @include themed() {
          color: t($primary);
        }
      }
    }
  }
}

.actions {
  flex-shrink: 0;
}
</style>
